import { Button } from '@wtfalch/design';

import type { LobbyState } from './useLobby.ts';

/**
 * The people at the door of a call. Any member in the call can let a guest in
 * or turn them away; whoever does is on the record. A guest's name is drawn as
 * plain text (it has been cleaned in `useLobby`).
 */
export function Lobby({ lobby }: { lobby: LobbyState }) {
  const { waiting, error, notes, busy } = lobby;
  return (
    <section aria-label="Who is joining" className="flex flex-col gap-3">
      <h3 className="h3 m-0">Who is joining</h3>
      {error && (
        <p className="m-0 text-xs text-bad" role="alert">
          {error}
        </p>
      )}
      {waiting.length === 0 && !error ? (
        <p className="m-0 text-sm text-muted">Nobody is waiting at the door.</p>
      ) : (
        <ul className="flex flex-col gap-3 m-0 p-0 list-none">
          {waiting.map((guest) => (
            <li key={guest.admissionId} className="flex flex-col gap-2">
              <span className="flex flex-col">
                <span className="text-sm font-strong">{guest.name}</span>
                <span className="text-xs text-muted">Guest · followed the link</span>
              </span>
              <span className="flex gap-2">
                <Button
                  size="sm"
                  kind="ghost"
                  aria-label={`Refuse ${guest.name}`}
                  disabled={busy.has(guest.admissionId)}
                  onPress={() => lobby.refuse(guest.admissionId)}
                >
                  Not now
                </Button>
                <Button
                  size="sm"
                  kind="primary"
                  aria-label={`Let ${guest.name} in`}
                  disabled={busy.has(guest.admissionId)}
                  onPress={() => lobby.admit(guest.admissionId)}
                >
                  Let them in
                </Button>
              </span>
              {notes[guest.admissionId] && (
                <span className="text-xs text-bad" role="alert">
                  {notes[guest.admissionId]}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      <p className="m-0 text-xs text-muted">
        Nobody from outside is in the room until one of you says so, and whoever lets them in is on
        the record.
      </p>
    </section>
  );
}
